import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { IconButton } from 'jl-design-system/components/button/Button';
import { ChevronLeftOutlined } from 'jl-design-system/components/icons/ChevronLeftOutlined';
import { ChevronRightOutlined } from 'jl-design-system/components/icons/ChevronRightOutlined';
import styles from './prenav.scss';

import { TopLinks } from '../../link-list/TopLinks';
import type { Link, CarouselItem, CarouselContentMode } from '../../types';

interface PrenavProps {
  isFocused?: boolean;
  isStickyHeaderActive?: boolean;
  onFocus?: (() => void) | null;
  topLinksData?: Link[];
}

const carouselItems: CarouselItem[] = [
  {
    id: 'free-returns',
    text: 'Free returns.',
    ctaText: 'Find out more',
    href: '/customer-services/returns',
  },
];

export const Prenav = ({
  isFocused = false,
  isStickyHeaderActive = false,
  onFocus = null,
  topLinksData = [],
}: PrenavProps) => {
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [carouselContentMode, setCarouselContentMode] =
    useState<CarouselContentMode>('mobile');
  const currentItem = carouselItems[carouselIndex];
  const hasMultipleCarouselItems = carouselItems.length > 1;

  const showCarouselNku = carouselContentMode === 'mobile';
  const showCarouselMessage = carouselContentMode === 'large';
  const showLeftNku = carouselContentMode === 'large';

  useEffect(() => {
    if (!window?.matchMedia) {
      setCarouselContentMode('large');
      return;
    }

    const desktopMediaQuery = window.matchMedia('(min-width: 1280px)');

    const updateCarouselContentMode = () => {
      if (desktopMediaQuery.matches) {
        setCarouselContentMode('large');
      } else {
        setCarouselContentMode('mobile');
      }
    };

    updateCarouselContentMode();

    // Check if addEventListener is supported (modern browsers)
    if (desktopMediaQuery.addEventListener) {
      desktopMediaQuery.addEventListener('change', updateCarouselContentMode);
    } else {
      // Fallback for Safari 13 and older browsers
      desktopMediaQuery.addListener(updateCarouselContentMode);
    }

    return () => {
      // Clean up with the same method
      if (desktopMediaQuery.removeEventListener) {
        desktopMediaQuery.removeEventListener(
          'change',
          updateCarouselContentMode
        );
      } else {
        desktopMediaQuery.removeListener(updateCarouselContentMode);
      }
    };
  }, []);

  const showPrevious = () => {
    setCarouselIndex(prev =>
      prev === 0 ? carouselItems.length - 1 : prev - 1
    );
  };

  const showNext = () => {
    setCarouselIndex(prev =>
      prev === carouselItems.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div
      className={classNames(styles.topBar, {
        [styles.topBar__collapsed]: isFocused || isStickyHeaderActive,
      })}
      data-testid="header-top-bar"
      onFocus={onFocus || undefined}
    >
      <div className={styles.pageContainer}>
        <div className={styles.grid}>
          <div
            className={classNames(styles.topLinks)}
            data-testid="header-top-links"
          >
            {showLeftNku && (
              <span className={styles.nkuContainer}>
                <a
                  href="/content/never-knowingly-undersold"
                  className={styles.nkuLink}
                >
                  NEVER KNOWINGLY UNDERSOLD
                </a>
              </span>
            )}
            <div className={styles.carouselContainer}>
              {showCarouselNku && (
                <a
                  href="/content/never-knowingly-undersold"
                  className={styles.carouselNkuLink}
                >
                  NEVER KNOWINGLY UNDERSOLD
                </a>
              )}
              {showCarouselMessage && hasMultipleCarouselItems && (
                <IconButton
                  icon={ChevronLeftOutlined}
                  size="small"
                  background="clear"
                  className={styles.chevronIcon}
                  aria-label="Previous message"
                  onClick={showPrevious}
                />
              )}
              {showCarouselMessage && (
                <p className={styles.carouselMessage}>
                  <span>{currentItem.text}</span>{' '}
                  <a href={currentItem.href} className={styles.carouselLink}>
                    {currentItem.ctaText}
                  </a>
                </p>
              )}
              {showCarouselMessage && hasMultipleCarouselItems && (
                <IconButton
                  icon={ChevronRightOutlined}
                  size="small"
                  background="clear"
                  className={styles.chevronIcon}
                  aria-label="Next message"
                  onClick={showNext}
                />
              )}
            </div>
            <TopLinks links={topLinksData} />
          </div>
        </div>
      </div>
    </div>
  );
};
